import {
  AutoComplete,
  BasicInput,
  Button,
  Col,
  FilterUtils,
  FormikInputDate,
  FormikInputInteger,
  FormikSelect,
  Loading,
  Modal,
  Panel,
  Row,
  SectionTitle
} from '@elotech/components';
import { useLoading } from 'common/hooks';
import {
  ArquivoService,
  ModeloDadosExporterService,
  ModeloDadosService
} from 'common/service';
import { ArquivoUrl, ModeloDados, ParamGroup } from 'common/type';
import {
  CategoriaModeloEnum,
  ClassificacaoOperacaoEnum,
  FilePathEnum,
  FileType,
  TipoModeloEnum,
  TipoParametroModeloEnum
} from 'common/type/enum';
import FileSaver from 'file-saver';
import { Form, Formik } from 'formik';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormGroup } from 'react-bootstrap';

import { InlineButton } from '../inline-button';
import { Label } from '../label';
import { TinyMCE, useTinyMCEAction } from '../tiny-mce';
import { groupBy } from './utils';

const ParamFields: Record<string, any> = {
  [TipoParametroModeloEnum.DATE]: FormikInputDate,
  [TipoParametroModeloEnum.STRING]: BasicInput,
  [TipoParametroModeloEnum.BOOLEAN]: FormikSelect,
  [TipoParametroModeloEnum.NUMBER]: FormikInputInteger
};

type Cabecalho = {
  id?: string;
  descricao?: string;
};

type CabecalhoService = {
  findAll: (search: string) => Promise<any>;
};

type DocumentoOffice = {
  blob: Blob;
  extensao: string;
};

export type ModeloDadosSearchComponentProps = {
  modeloDadosService: ModeloDadosService;
  exporterService: ModeloDadosExporterService;
  baseUrlArquivo: string;
  entidadeId?: string | number;
  cabecalhoService?: CabecalhoService;
  template?: string;
  onUpdateTemplate: (templateHtml: string) => void;
  disableSearch?: boolean;
  categoria?: CategoriaModeloEnum | '';
  classificacao?: ClassificacaoOperacaoEnum;
  entityId?: string;
  resource?: string;
  filePath?: FilePathEnum;
  setLoadingArquivos?: (value: any) => void;
  editorId?: string;
  /** Exibe o modal de parâmetros; quando falso gera direto com os valores padrão. */
  exibirParametros?: boolean;
  podeSalvarAnexo?: boolean;
  onAnexoSalvo?: () => void;
  descricaoSalvarAnexo?: string;
  /** Gera sem abrir o modal quando todos os parâmetros são `id`. */
  gerarDiretoSeSomenteId?: boolean;
  /** Seleciona automaticamente o modelo quando a categoria possui apenas um. */
  carregarModeloUnico?: boolean;
  /** Guarda o documento Office gerado para baixar novamente ou salvar como anexo. */
  officeComAnexo?: boolean;
  labelBotaoParametros?: string;
  ocultarBotaoParametros?: boolean;
  getOptionLabel?: (modelo: ModeloDados) => string;
  /** Substitui o modal padrão de exportação de PDF. */
  onExportPdf?: (template: string) => Promise<any> | undefined;
  onChangeModelo?: (modelo?: ModeloDados) => void;
  children?: ReactNode;
  labelTemplate?: string;
};

const getExtensao = (nomeArquivo?: string) => {
  const index = nomeArquivo?.lastIndexOf('.') ?? -1;
  return index >= 0 ? nomeArquivo!.substring(index) : '.docx';
};

const ModeloDadosSearchComponent = ({
  modeloDadosService,
  exporterService,
  baseUrlArquivo,
  entidadeId,
  cabecalhoService,
  template = '',
  onUpdateTemplate,
  disableSearch = false,
  categoria = CategoriaModeloEnum.LICITACAO,
  classificacao,
  entityId = '',
  resource = '',
  filePath,
  setLoadingArquivos,
  editorId = 'modelo-dados-editor',
  exibirParametros = true,
  podeSalvarAnexo = true,
  onAnexoSalvo,
  descricaoSalvarAnexo = 'Deseja salvar o modelo como anexo? Se sim, informe o nome que o pdf receberá.',
  gerarDiretoSeSomenteId = false,
  carregarModeloUnico = true,
  officeComAnexo = false,
  labelBotaoParametros = 'Salvar Parâmetros',
  ocultarBotaoParametros = false,
  getOptionLabel = modelo => `${modelo.nome} - ${modelo.tipo}`,
  onExportPdf,
  onChangeModelo: onModeloChange,
  children,
  labelTemplate = 'Template'
}: ModeloDadosSearchComponentProps) => {
  const [loading, setLoading] = useLoading();
  const [modelo, setModelo] = useState<ModeloDados>();
  const [paramGroups, setParamGroups] = useState<ParamGroup[]>([]);
  const [formParams, setFormParams] = useState<any[]>([]);
  const [showParamsModal, setShowParamsModal] = useState(false);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [cabecalho, setCabecalho] = useState<Cabecalho>();
  const [showCabecalho, setShowCabecalho] = useState(false);
  const [documentoOffice, setDocumentoOffice] = useState<DocumentoOffice>();
  const [showSalvarOffice, setShowSalvarOffice] = useState(false);

  const exportAction = useTinyMCEAction();

  const arquivoService = useMemo(
    () => new ArquivoService(baseUrlArquivo, resource),
    [baseUrlArquivo, resource]
  );

  useEffect(() => {
    onModeloChange?.(modelo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelo]);

  const classificacaoSearch = classificacao
    ? ` and classificacaoOperacao==${classificacao}`
    : '';

  useEffect(() => {
    if (!carregarModeloUnico) {
      return;
    }

    modeloDadosService
      .findAll(`categoria==${categoria}${classificacaoSearch}`)
      .then(({ data }) => {
        if (data?.totalElements === 1 && !disableSearch) {
          setModelo(data.content[0]);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cabecalhoService?.findAll('ativo==true').then(({ data }) => {
      setShowCabecalho(data?.totalElements > 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = (modeloDados: ModeloDados, paramValues: any[] = []) => {
    setShowParamsModal(false);
    if (modeloDados.tipo === TipoModeloEnum.OFFICE) {
      return setLoading(
        modeloDadosService
          .previewOffice({ modeloDados, paramValues })
          .then(({ data, headers }) => {
            const blob = new Blob([data], {
              type: (headers['content-type'] as FileType) || FileType.DOCX
            });
            if (officeComAnexo) {
              setDocumentoOffice({
                blob,
                extensao: getExtensao(modeloDados.arquivoNome)
              });
            }
            FileSaver.saveAs(blob, modeloDados.nome);
          })
      );
    }

    return setLoading(
      modeloDadosService
        .preview({ modeloDados, paramValues })
        .then(({ data }) => onUpdateTemplate(data))
    );
  };

  const createParamsAgrupadosBySql = (modeloDados: ModeloDados) =>
    groupBy(
      modeloDados.sqls!.flatMap(s => s.sqlModelo!.parametros),
      'sqlIdentificador'
    );

  const createFormObject = (groups: ParamGroup[]) =>
    groups.map(group => ({
      [group.field]: group.groupList.reduce(
        (params, p) => ({
          ...params,
          [p.identificador!]: p.identificador === 'id' ? entityId : undefined
        }),
        {}
      )
    }));

  const modeloHasSqls = (modeloDados?: ModeloDados) =>
    !!modeloDados?.sqls?.length;

  const initParams = (modeloDados: ModeloDados) => {
    const groups = createParamsAgrupadosBySql(modeloDados);
    const values = createFormObject(groups);
    setParamGroups(groups);
    setFormParams(values);
    return { groups, values };
  };

  const onChangeModelo = (_, value?: ModeloDados) => {
    setModelo(value);
    setParamGroups([]);
    setFormParams([]);
    setDocumentoOffice(undefined);

    if (!value) {
      return;
    }

    if (!modeloHasSqls(value)) {
      return preview(value);
    }

    const { groups, values } = initParams(value);
    const somenteId = groups.every(group =>
      group.groupList.every(p => p.identificador === 'id')
    );

    if (!exibirParametros || (gerarDiretoSeSomenteId && somenteId)) {
      return preview(value, values);
    }

    setShowParamsModal(true);
  };

  const abrirParametros = () => {
    if (formParams.length === 0) {
      initParams(modelo!);
    }
    setShowParamsModal(true);
  };

  const getParamType = (groupKey: string, paramKey: string) =>
    paramGroups
      .find(p => p.field === groupKey)
      ?.groupList?.find(p => p.identificador === paramKey)?.tipo ||
    TipoParametroModeloEnum.STRING;

  const renderParam = (
    groupKey: string,
    groupIndex: number,
    paramKey: string
  ) => {
    const type = getParamType(groupKey, paramKey);
    const ParamField = ParamFields[type];
    const booleanProps =
      type === TipoParametroModeloEnum.BOOLEAN
        ? {
            getOptionLabel: value => (value === 'S' ? 'Sim' : 'Não'),
            getOptionValue: value => value,
            options: ['S', 'N']
          }
        : {};

    return ParamField ? (
      <ParamField
        key={`${groupIndex}_${paramKey}`}
        size={3}
        name={`${groupIndex}.${groupKey}.${paramKey}`}
        label={paramKey}
        disabled={loading || (gerarDiretoSeSomenteId && paramKey === 'id')}
        {...booleanProps}
      />
    ) : null;
  };

  const hasParamsVazios = (values: any[]) =>
    values.some(value => {
      const fields = Object.values(value)[0] as object;
      return fields && Object.values(fields).some(field => !field);
    });

  const renderParamsModal = () => (
    <Modal onClose={() => setShowParamsModal(false)} maxWidth="75%">
      <Panel title="Inserir Parâmetros" isForm>
        <Formik<any[]>
          enableReinitialize
          initialValues={formParams}
          onSubmit={values => {
            setFormParams(values);
            preview(modelo!, values);
          }}
        >
          {formProps => (
            <Form>
              {formProps.values.map((group, groupIndex) => {
                const groupKey = Object.keys(group)[0];
                return groupKey ? (
                  <div key={`${groupIndex}_${groupKey}`}>
                    <SectionTitle>{groupKey}</SectionTitle>
                    <Row>
                      {Object.keys(group[groupKey]).map(paramKey =>
                        renderParam(groupKey, groupIndex, paramKey)
                      )}
                    </Row>
                  </div>
                ) : null;
              })}
              <Row>
                <Col md={12} sm={12} className="mt-xs right">
                  <FormGroup>
                    <InlineButton
                      label={labelBotaoParametros}
                      onClick={formProps.submitForm}
                      disabled={hasParamsVazios(formProps.values)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Panel>
    </Modal>
  );

  const salvarAnexo = async (blob: Blob, fileName: string) => {
    const { data: arquivoUrl } = await arquivoService.gerarUrlParaUpload(
      filePath!,
      fileName,
      blob.type
    );

    await arquivoService.enviarArquivoParaServidor(
      blob as File,
      (arquivoUrl as ArquivoUrl).urlAssinada
    );

    const salvarEntity = arquivoService.salvarArquivoEntity(entityId, {
      domainEntity: { id: entityId },
      arquivo: {
        nome: fileName,
        mimeType: blob.type,
        dataCriacao: arquivoUrl.dataCriacao,
        fullPath: arquivoUrl.fullPath
      }
    });
    await (setLoadingArquivos
      ? setLoadingArquivos(salvarEntity)
      : salvarEntity);

    onAnexoSalvo?.();
  };

  const exportarPdf = async (salvar: boolean) => {
    const modeloTemplate = { ...modelo, templateHtml: template };
    const cabecalhoArgs: [string?] = cabecalho?.id ? [cabecalho.id] : [];

    await setLoading(
      (async () => {
        if (salvar) {
          const { data } = await exporterService.getBlobFile(
            modeloTemplate,
            ...cabecalhoArgs
          );
          await salvarAnexo(data, `${nomeArquivo}.pdf`);
        }
        await exporterService.exportarModeloPdf(
          modeloTemplate,
          ...cabecalhoArgs
        );
      })()
    );

    setNomeArquivo('');
    exportAction.finish();
  };

  const salvarOffice = async () => {
    await setLoading(
      salvarAnexo(
        documentoOffice!.blob,
        `${nomeArquivo}${documentoOffice!.extensao}`
      )
    );
    setNomeArquivo('');
    setShowSalvarOffice(false);
  };

  const renderNomeArquivo = (descricao: string) => (
    <Row>
      <Col sm={12} md={12}>
        <FormGroup>
          {descricao}
          <input
            type="text"
            id="nomeArquivo"
            name="nomeArquivo"
            onChange={event => setNomeArquivo(event.target.value)}
            value={nomeArquivo}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  const renderModalExportPdf = () => (
    <Modal onClose={exportAction.finish} maxWidth="30%">
      <Panel title="Exportar" isForm>
        {cabecalhoService && showCabecalho && (
          <Row>
            <Col sm={12} md={12}>
              <FormGroup>
                <Label htmlFor="cabecalho">Cabeçalho</Label>
                <AutoComplete<Cabecalho>
                  name="cabecalho"
                  value={cabecalho}
                  isDisabled={disableSearch}
                  onSearch={search =>
                    cabecalhoService.findAll(
                      `ativo==true ${
                        search ? ` and descricao==%${search}%` : ''
                      }`
                    )
                  }
                  getOptionLabel={cabecalho => `${cabecalho?.descricao}`}
                  onSelect={(_, value) => setCabecalho(value)}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {podeSalvarAnexo && renderNomeArquivo(descricaoSalvarAnexo)}
        <Row>
          <FormGroup>
            {podeSalvarAnexo && (
              <div className="col-md-2">
                <Button
                  disabled={!nomeArquivo}
                  type="button"
                  className="btn mr-xs icon-left positive"
                  onClick={() => exportarPdf(true)}
                >
                  <em className="fa fa-save" />
                  Sim
                </Button>
              </div>
            )}
            <div className="col-md-2">
              <Button
                type="button"
                className="btn mr-xs icon-left"
                onClick={() => exportarPdf(false)}
              >
                <em className="fa fa-print" />
                {podeSalvarAnexo ? 'Não' : 'Imprimir'}
              </Button>
            </div>
          </FormGroup>
        </Row>
      </Panel>
    </Modal>
  );

  const renderModalSalvarOffice = () => (
    <Modal onClose={() => setShowSalvarOffice(false)} maxWidth="30%">
      <Panel title="Salvar como anexo" isForm>
        {renderNomeArquivo('Informe o nome que o arquivo receberá.')}
        <Row>
          <Col md={12} className="right">
            <Button
              disabled={!nomeArquivo}
              type="button"
              className="btn icon-left positive"
              onClick={salvarOffice}
            >
              <em className="fa fa-save" />
              Salvar
            </Button>
          </Col>
        </Row>
      </Panel>
    </Modal>
  );

  const onSearchModelo = (search: string) => {
    let customSearch = 'codigo==null and ativo==true and ';
    customSearch += FilterUtils.buildFilter(['nome'])(search);

    if (entidadeId) {
      customSearch += ` and entidade==${entidadeId}`;
    }

    if (categoria) {
      customSearch += ` and categoria==${categoria}`;
    }

    return modeloDadosService.findAll(customSearch + classificacaoSearch);
  };

  const exibirBotaoParametros =
    modeloHasSqls(modelo) && exibirParametros && !ocultarBotaoParametros;
  const exibirOffice = officeComAnexo && modelo?.tipo === TipoModeloEnum.OFFICE;

  return (
    <>
      {showParamsModal && renderParamsModal()}
      {exportAction.running && renderModalExportPdf()}
      {showSalvarOffice && renderModalSalvarOffice()}
      <Loading loading={loading} />
      <Row>
        <Col
          sm={exibirBotaoParametros ? 10 : 12}
          md={exibirBotaoParametros ? 10 : 12}
        >
          <FormGroup>
            <Label htmlFor="modelo">Modelo</Label>
            <AutoComplete<ModeloDados>
              name="modelo"
              value={modelo}
              isDisabled={disableSearch}
              onSearch={onSearchModelo}
              getOptionLabel={getOptionLabel}
              onSelect={onChangeModelo}
            />
          </FormGroup>
        </Col>
        {exibirBotaoParametros && (
          <Col md={2} sm={2} className="mt-xs">
            <FormGroup>
              <InlineButton
                icon="fa fa-pencil-alt"
                label="Parâmetros"
                onClick={abrirParametros}
              />
            </FormGroup>
          </Col>
        )}
      </Row>
      {exibirOffice ? (
        <Row>
          <Col md={12}>
            <InlineButton
              icon="fa fa-download"
              label="Baixar novamente"
              disabled={!documentoOffice}
              onClick={() =>
                FileSaver.saveAs(documentoOffice!.blob, modelo!.nome)
              }
            />
            {podeSalvarAnexo && (
              <InlineButton
                icon="fa fa-save"
                label="Salvar como anexo"
                disabled={!documentoOffice}
                onClick={() => setShowSalvarOffice(true)}
              />
            )}
          </Col>
        </Row>
      ) : (
        <FormGroup>
          <Label>{labelTemplate}</Label>
          <TinyMCE
            editorId={editorId}
            value={template}
            onChange={onUpdateTemplate}
            actions={[
              {
                name: 'exportpdf',
                execute: value =>
                  onExportPdf
                    ? value
                      ? onExportPdf(value)
                      : undefined
                    : exportAction.init()
              }
            ]}
          />
        </FormGroup>
      )}
      {children}
    </>
  );
};

export { ModeloDadosSearchComponent };
